import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./DashboardLayout";
import { api, API_URL, getErrorMessage, getToken } from "@/lib/api";
import { Plus, Play, Square, Trash2, Loader2, FileAudio, Copy, Check, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";

const API_BASE = API_URL || "";

export default function Articles() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [playingId, setPlayingId] = useState(null);
    const [loadingAudioId, setLoadingAudioId] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [regenIds, setRegenIds] = useState(new Set());
    const audioRef = useRef(null);
    const pollRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/articles");
            setArticles(data);
            return data;
        } catch (e) { toast.error(getErrorMessage(e)); return []; }
        finally { setLoading(false); }
    }, []);

    const startPolling = useCallback(() => {
        if (pollRef.current) return;
        pollRef.current = setInterval(async () => {
            const data = await load();
            if (!data.some(a => a.status === "processing")) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        }, 3000);
    }, [load]);

    useEffect(() => {
        load().then(data => {
            if (data.some(a => a.status === "processing")) startPolling();
        });
    }, [load, startPolling]);

    // Stop audio when leaving
    useEffect(() => () => {
        audioRef.current?.pause();
        if (pollRef.current) clearInterval(pollRef.current);
    }, []);

    const onPlay = async (a) => {
        if (playingId === a.id) {
            audioRef.current?.pause();
            setPlayingId(null);
            return;
        }
        if (a.status === "processing") {
            toast.info("Audio is still being generated. Please wait.");
            return;
        }
        if (!a.audio_url) {
            toast.error("No audio for this article. Try regenerating.");
            return;
        }
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        setLoadingAudioId(a.id);
        try {
            const res = await fetch(`${API_BASE}${a.audio_url}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || "Audio unavailable.");
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audioRef.current = audio;

            // Track play progress for analytics
            let lastMilestone = 0;
            const reportProgress = (pct) => {
                api.post(`/articles/${a.id}/play-progress`, { completion_pct: pct }).catch(() => {});
            };
            audio.ontimeupdate = () => {
                if (!audio.duration) return;
                const pct = Math.round((audio.currentTime / audio.duration) * 100);
                const milestone = Math.floor(pct / 10) * 10;
                if (milestone > lastMilestone && milestone > 0) {
                    lastMilestone = milestone;
                    reportProgress(milestone);
                }
            };
            audio.onended = () => {
                reportProgress(100);
                setPlayingId(null);
            };
            audio.onerror = () => { toast.error("Playback failed"); setPlayingId(null); };
            await audio.play();
            setPlayingId(a.id);
        } catch (err) {
            toast.error("Failed to load audio.");
        } finally { setLoadingAudioId(null); }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this article and its audio?")) return;
        if (playingId === id) { audioRef.current?.pause(); setPlayingId(null); }
        try {
            await api.delete(`/articles/${id}`);
            setArticles(articles.filter(a => a.id !== id));
            toast.success("Article deleted");
        } catch (e) { toast.error(getErrorMessage(e)); }
    };

    const onDownload = async (a) => {
        if (!a.audio_url || a.status !== "ready") {
            toast.error("No audio available to download.");
            return;
        }
        try {
            const res = await fetch(`${API_BASE}${a.audio_url}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) { toast.error("Download failed."); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${a.title || a.id}.mp3`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success("Download started");
        } catch (err) { toast.error("Download failed."); }
    };

    const onRegenerate = async (a) => {
        setRegenIds(s => new Set([...s, a.id]));
        try {
            const { data } = await api.post(`/articles/${a.id}/regenerate`);
            setArticles(prev => prev.map(x => x.id === a.id ? data : x));
            toast.success("Regenerating audio…");
            startPolling();
        } catch (e) {
            toast.error(getErrorMessage(e));
        } finally {
            setRegenIds(s => { const n = new Set(s); n.delete(a.id); return n; });
        }
    };

    const copyEmbed = async (id) => {
        const code = `<script src="https://audo.dev/embed/v1.js" data-article="${id}"></script>`;
        try { await navigator.clipboard.writeText(code); } catch {}
        setCopiedId(id);
        toast.success("Embed code copied");
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <div data-testid="articles-page">
            <PageHeader
                eyebrow="Articles"
                title="All your audio."
                subtitle="Generated articles, plays, and completion at a glance."
                action={
                    <Link to="/dashboard/articles/new" data-testid="new-article-btn" className="bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-3 text-[14px] font-semibold inline-flex items-center gap-2 transition-colors">
                        <Plus size={14}/> New article
                    </Link>
                }
            />

            <div className="bg-white rounded-3xl border border-cream-300 shadow-soft overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-ink-muted">
                        <Loader2 className="animate-spin mx-auto mb-3" size={22}/>
                        Loading…
                    </div>
                ) : articles.length === 0 ? (
                    <div className="p-14 text-center">
                        <FileAudio className="mx-auto mb-4 text-ink-muted" size={36}/>
                        <h3 className="font-display text-[26px] font-semibold tracking-tight mb-2">No articles yet</h3>
                        <p className="text-[14px] text-ink-muted mb-5 max-w-sm mx-auto">Paste a Bengali article and generate beautiful audio in 30 seconds.</p>
                        <Link to="/dashboard/articles/new" className="inline-flex items-center gap-2 bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-3 text-[14px] font-semibold transition-colors">
                            <Plus size={14}/> Create your first
                        </Link>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-cream-100">
                            <tr className="text-left">
                                <th className="px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Title</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted hidden md:table-cell">Voice</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted hidden md:table-cell">Length</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Plays</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Status</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted hidden lg:table-cell">Embed</th>
                                <th className="px-4 py-3"/>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-cream-300">
                            {articles.map(a => {
                                const isPlaying = playingId === a.id;
                                const isLoading = loadingAudioId === a.id;
                                const isProcessing = a.status === "processing";
                                const hasAudio = !!a.audio_url && a.status === "ready";
                                const isRegen = regenIds.has(a.id);
                                const statusColor = a.status === "ready"
                                    ? "bg-mint/30 text-ink"
                                    : isProcessing
                                        ? "bg-amber-100 text-amber-700"
                                        : "bg-red-50 text-red-500";
                                const statusDot = a.status === "ready"
                                    ? "bg-mint"
                                    : isProcessing ? "bg-amber-400 animate-pulse" : "bg-red-400";
                                return (
                                <tr key={a.id} data-testid={`article-row-${a.id}`} className="hover:bg-cream-100 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => onPlay(a)}
                                                title={isProcessing ? "Generating…" : hasAudio ? (isPlaying ? "Stop" : "Play") : "No audio"}
                                                className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                                                    isProcessing ? "bg-amber-100 text-amber-600 cursor-wait"
                                                    : hasAudio ? "bg-coral hover:bg-ink text-white"
                                                    : "bg-cream-300 text-ink-muted cursor-not-allowed"
                                                }`}
                                            >
                                                {isLoading ? <Loader2 size={13} className="animate-spin"/>
                                                    : isProcessing ? <Loader2 size={13} className="animate-spin"/>
                                                    : isPlaying ? <Square size={11} fill="currentColor"/>
                                                    : <Play size={11} className="ml-0.5" fill="currentColor"/>
                                                }
                                            </button>
                                            <span className="font-medium text-ink text-[14px]">{a.title}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-[13px] text-ink-muted capitalize hidden md:table-cell">{a.voice_id}</td>
                                    <td className="px-4 py-4 text-[13px] text-ink-muted font-mono hidden md:table-cell">
                                        {Math.floor(a.duration_sec / 60)}:{(a.duration_sec % 60).toString().padStart(2, "0")}
                                    </td>
                                    <td className="px-4 py-4 text-[13px] text-ink font-mono">{a.plays}</td>
                                    <td className="px-4 py-4">
                                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${statusColor}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`}/>
                                            {a.status === "no_audio" ? "no audio" : a.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 hidden lg:table-cell">
                                        <button onClick={() => copyEmbed(a.id)}
                                            title="Copy embed code"
                                            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${copiedId === a.id ? "border-mint bg-mint/20 text-ink" : "border-cream-300 hover:border-coral hover:text-coral text-ink-muted"}`}>
                                            {copiedId === a.id ? <Check size={11}/> : <Copy size={11}/>}
                                            {copiedId === a.id ? "Copied" : "Embed"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-4 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {hasAudio && (
                                                <button onClick={() => onDownload(a)}
                                                    title="Download audio"
                                                    className="p-2 rounded-lg hover:bg-blue-50 text-ink-muted hover:text-blue-500 transition-colors">
                                                    <Download size={14}/>
                                                </button>
                                            )}
                                            {a.status === "no_audio" && (
                                                <button onClick={() => onRegenerate(a)} disabled={isRegen}
                                                    title="Regenerate audio"
                                                    className="p-2 rounded-lg hover:bg-coral/10 text-ink-muted hover:text-coral transition-colors disabled:opacity-50">
                                                    <RefreshCw size={14} className={isRegen ? "animate-spin" : ""}/>
                                                </button>
                                            )}
                                            <button onClick={() => onDelete(a.id)} data-testid={`delete-${a.id}`}
                                                className="p-2 rounded-lg hover:bg-coral/10 text-ink-muted hover:text-coral transition-colors">
                                                <Trash2 size={14}/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
